<template>
    <v-dialog
        :value="value"
        max-width="760"
        persistent
        scrollable
        @input="onVisibility"
    >
        <v-card class="publish-card" v-if="article" elevation="0">
            <v-card-title class="d-flex align-center pb-2">
                <div class="mr-3">
                    <eyebrow>Finalizar artigo</eyebrow>
                    <div class="card-title-strong one-line-text mt-1">
                        {{ article.title || 'Publicar artigo' }}
                    </div>
                </div>
                <v-spacer />
                <span :class="['pill', statusMeta.pillClass]">
                    <v-icon left small :color="statusMeta.iconColor">{{ statusMeta.icon }}</v-icon>
                    {{ statusMeta.label }}
                </span>
            </v-card-title>

            <v-divider />

            <v-card-text class="publish-body">
                <ArtigosImagePicker
                    v-model="publishMainImage"
                    :disabled="!canEdit"
                />

                <div class="field-block">
                    <label class="field-label">Autor</label>
                    <v-text-field
                        v-model="publishAuthor"
                        outlined
                        dense
                        hide-details
                        placeholder="Nome do autor que aparecerá na publicação"
                        :disabled="!canEdit"
                    />
                </div>

                <div class="field-block" v-if="canEdit">
                    <label class="field-label">Quando publicar?</label>
                    <div class="mode-switch">
                        <button
                            type="button"
                            class="mode-option"
                            :class="{ 'mode-option--active': publishMode === 'now' }"
                            @click="publishMode = 'now'"
                        >
                            <v-icon :color="publishMode === 'now' ? 'primary' : 'mercury'">mdi-send-outline</v-icon>
                            <span class="mode-option-title">Publicar agora</span>
                            <span class="mode-option-hint">Envia imediatamente para o site</span>
                        </button>
                        <button
                            type="button"
                            class="mode-option"
                            :class="{ 'mode-option--active': publishMode === 'schedule' }"
                            @click="publishMode = 'schedule'"
                        >
                            <v-icon :color="publishMode === 'schedule' ? 'primary' : 'mercury'">mdi-calendar-clock-outline</v-icon>
                            <span class="mode-option-title">Agendar publicação</span>
                            <span class="mode-option-hint">Escolha a data e hora</span>
                        </button>
                    </div>
                </div>

                <transition name="slide-fade">
                    <div v-if="canEdit && publishMode === 'schedule'" class="field-block">
                        <label class="field-label">Data e hora</label>

                        <div class="shortcut-row mb-2">
                            <button
                                v-for="s in shortcuts"
                                :key="s.key"
                                type="button"
                                class="shortcut-chip"
                                :class="{ 'shortcut-chip--active': isActiveShortcut(s) }"
                                @click="applyShortcut(s)"
                            >
                                <v-icon small :color="isActiveShortcut(s) ? 'primary' : 'mercury'">mdi-clock-fast</v-icon>
                                <span>{{ s.label }}</span>
                            </button>
                        </div>

                        <div class="datetime-row">
                            <v-menu
                                v-model="dateMenuOpen"
                                :close-on-content-click="false"
                                offset-y
                                min-width="290"
                            >
                                <template #activator="{ on, attrs }">
                                    <v-text-field
                                        :value="displayDate"
                                        label="Data"
                                        outlined
                                        dense
                                        readonly
                                        prepend-inner-icon="mdi-calendar"
                                        hide-details
                                        v-bind="attrs"
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="dateValue"
                                    :min="todayISO"
                                    locale="pt-BR"
                                    @input="dateMenuOpen = false"
                                />
                            </v-menu>

                            <v-menu
                                v-model="timeMenuOpen"
                                :close-on-content-click="false"
                                offset-y
                                min-width="290"
                            >
                                <template #activator="{ on, attrs }">
                                    <v-text-field
                                        :value="timeValue"
                                        label="Hora"
                                        outlined
                                        dense
                                        readonly
                                        prepend-inner-icon="mdi-clock-outline"
                                        hide-details
                                        v-bind="attrs"
                                        v-on="on"
                                    />
                                </template>
                                <v-time-picker
                                    v-model="timeValue"
                                    format="24hr"
                                    :allowed-minutes="allowedMinutes"
                                    @click:minute="timeMenuOpen = false"
                                />
                            </v-menu>
                        </div>

                        <div v-if="scheduleInvalid" class="schedule-warning">
                            <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
                            A data deve ser pelo menos 5 minutos no futuro.
                        </div>
                    </div>
                </transition>

                <div v-if="article.publish_status === 'scheduled' && canEdit" class="scheduled-info">
                    <v-icon small color="warning" class="mr-1">mdi-information-outline</v-icon>
                    Este artigo está agendado para
                    <strong class="ml-1">{{ formattedScheduledDate }}</strong>.
                    Altere a data acima para reagendar ou use "Cancelar agendamento".
                </div>

                <div v-if="article.publish_status === 'published'" class="published-info">
                    <v-icon color="success" class="mr-2">mdi-check-circle-outline</v-icon>
                    <div>
                        <div class="text-body-2 font-weight-medium stardust--text">
                            Artigo publicado em {{ formattedPublishDate }}
                        </div>
                        <a
                            v-if="article.duda_link"
                            :href="article.duda_link"
                            target="_blank"
                            rel="noopener"
                            class="text-caption"
                        >
                            Abrir no site
                            <v-icon small>mdi-open-in-new</v-icon>
                        </a>
                    </div>
                </div>
            </v-card-text>

            <v-divider />

            <v-card-actions class="publish-actions pt-2">
                <v-btn text @click="close" :disabled="submitting">
                    {{ article.publish_status === 'published' ? 'Fechar' : 'Cancelar' }}
                </v-btn>

                <v-btn
                    v-if="article.publish_status === 'scheduled' && canEdit"
                    text
                    color="error"
                    :disabled="submitting"
                    :loading="cancelingSchedule"
                    @click="cancelSchedule"
                >
                    Cancelar agendamento
                </v-btn>

                <v-spacer />

                <v-btn
                    v-if="canEdit"
                    text
                    :disabled="submitting || !hasAnyMetadataChange"
                    :loading="savingMetadata"
                    @click="saveMetadataOnly"
                >
                    Salvar alterações
                </v-btn>

                <v-btn
                    v-if="canEdit"
                    color="primary"
                    depressed
                    large
                    :loading="submitting"
                    :disabled="!canSubmit"
                    @click="submit"
                >
                    <v-icon left>{{ publishMode === 'now' ? 'mdi-send' : 'mdi-calendar-check' }}</v-icon>
                    {{ publishMode === 'now' ? 'Publicar agora' : 'Agendar publicação' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { format, addHours, addDays, startOfDay, nextMonday, setHours, setMinutes, setSeconds, setMilliseconds, isEqual } from 'date-fns'
import Api from '@/lib/Api'
import { emitToastr } from '@/lib/Utils'
import ArtigosImagePicker from '@/components/ArtigosImagePicker.vue'

const SAO_PAULO_UTC_OFFSET = '-03:00'

export default {
    name: 'ArtigosPublishDialog',
    components: { ArtigosImagePicker },
    props: {
        value: { type: Boolean, default: false },
        article: { type: Object, default: null },
    },
    data() {
        return {
            publishMainImage: '',
            publishAuthor: '',
            publishMode: 'now',
            dateValue: '',
            timeValue: '09:00',
            dateMenuOpen: false,
            timeMenuOpen: false,
            submitting: false,
            savingMetadata: false,
            cancelingSchedule: false,
            initialSnapshot: { image: '', author: '' },
        }
    },
    computed: {
        canEdit() {
            return this.article?.publish_status !== 'published'
        },
        statusMeta() {
            const s = this.article?.publish_status
            if (s === 'published') {
                return { label: 'Publicado', pillClass: 'pill--positive', iconColor: 'success', icon: 'mdi-check-circle' }
            }
            if (s === 'scheduled') {
                return { label: 'Agendado', pillClass: 'pill--primary', iconColor: 'primary', icon: 'mdi-calendar-clock' }
            }
            return { label: 'Rascunho', pillClass: 'pill--neutral', iconColor: 'mercury', icon: 'mdi-file-document-edit-outline' }
        },
        todayISO() {
            return format(new Date(), 'yyyy-MM-dd')
        },
        scheduledDate() {
            if (!this.dateValue || !this.timeValue) return null
            const [y, m, d] = this.dateValue.split('-').map(Number)
            const [hh, mm] = this.timeValue.split(':').map(Number)
            return new Date(y, m - 1, d, hh, mm, 0, 0)
        },
        scheduledDatePayload() {
            if (!this.dateValue || !this.timeValue) return null
            return `${this.dateValue}T${this.timeValue}:00${SAO_PAULO_UTC_OFFSET}`
        },
        scheduleInvalid() {
            if (this.publishMode !== 'schedule') return false
            if (!this.scheduledDate) return false
            return this.scheduledDate.getTime() < Date.now() + 5 * 60 * 1000
        },
        displayDate() {
            if (!this.dateValue) return ''
            const [y, m, d] = this.dateValue.split('-').map(Number)
            return format(new Date(y, m - 1, d), 'dd/MM/yyyy')
        },
        canSubmit() {
            if (!this.article) return false
            if (!this.publishMainImage) return false
            if (!this.publishAuthor || !this.publishAuthor.trim()) return false
            if (this.publishMode === 'schedule') {
                if (!this.dateValue || !this.timeValue) return false
                if (this.scheduleInvalid) return false
            }
            return true
        },
        formattedScheduledDate() {
            if (!this.article?.publish_date) return ''
            return this.$date.format(new Date(this.article.publish_date), 'dd/MM/yyyy HH:mm')
        },
        formattedPublishDate() {
            if (!this.article?.publish_date) return ''
            return this.$date.format(new Date(this.article.publish_date), 'dd/MM/yyyy HH:mm')
        },
        shortcuts() {
            const now = new Date()
            const in1h = addHours(now, 1)
            const tomorrow9 = setMilliseconds(setSeconds(setMinutes(setHours(addDays(startOfDay(now), 1), 9), 0), 0), 0)
            const nextMon9 = setMilliseconds(setSeconds(setMinutes(setHours(nextMonday(now), 9), 0), 0), 0)
            return [
                { key: 'in1h', label: 'Daqui 1 hora', date: in1h },
                { key: 'tomorrow9', label: 'Amanhã 9h', date: tomorrow9 },
                { key: 'nextMon9', label: 'Próxima segunda 9h', date: nextMon9 },
            ]
        },
        hasAnyMetadataChange() {
            if (!this.article) return false
            return this.publishMainImage !== this.initialSnapshot.image
                || this.publishAuthor !== this.initialSnapshot.author
        },
    },
    methods: {
        onVisibility(val) {
            this.$emit('input', val)
        },
        close() {
            this.$emit('input', false)
        },
        allowedMinutes(m) {
            return m % 5 === 0
        },
        applyShortcut(s) {
            const d = s.date
            this.dateValue = format(d, 'yyyy-MM-dd')
            this.timeValue = format(d, 'HH:mm')
        },
        isActiveShortcut(s) {
            if (!this.scheduledDate) return false
            return isEqual(
                new Date(this.scheduledDate.getFullYear(), this.scheduledDate.getMonth(), this.scheduledDate.getDate(), this.scheduledDate.getHours(), this.scheduledDate.getMinutes()),
                new Date(s.date.getFullYear(), s.date.getMonth(), s.date.getDate(), s.date.getHours(), s.date.getMinutes()),
            )
        },
        async submit() {
            if (!this.article || this.submitting || !this.canSubmit) return
            this.submitting = true
            try {
                if (this.publishMode === 'now') {
                    const resp = await Api.publishArticleNowV2({
                        article_id: this.article._id,
                        publish_main_image: this.publishMainImage,
                        publish_author: this.publishAuthor.trim(),
                    })
                    if (resp?.error) {
                        emitToastr('error', resp.message || 'Erro ao publicar artigo.')
                        return
                    }
                    emitToastr('success', 'Artigo publicado com sucesso!')
                    this.$emit('submit', resp?.message?.article)
                    this.$emit('input', false)
                } else {
                    const resp = await Api.scheduleArticleV2({
                        article_id: this.article._id,
                        publish_date: this.scheduledDatePayload,
                        publish_main_image: this.publishMainImage,
                        publish_author: this.publishAuthor.trim(),
                    })
                    if (resp?.error) {
                        emitToastr('error', resp.message || 'Erro ao agendar artigo.')
                        return
                    }
                    emitToastr('success', 'Publicação agendada com sucesso!')
                    this.$emit('submit', resp?.message?.article)
                    this.$emit('input', false)
                }
            } catch (err) {
                emitToastr('error', err?.message || 'Erro ao processar publicação.')
            } finally {
                this.submitting = false
            }
        },
        async saveMetadataOnly() {
            if (!this.article || this.savingMetadata) return
            this.savingMetadata = true
            try {
                const resp = await Api.updateArticleMetadataV2({
                    article_id: this.article._id,
                    publish_main_image: this.publishMainImage,
                    publish_author: this.publishAuthor.trim(),
                })
                if (resp?.error) {
                    emitToastr('error', resp.message || 'Erro ao salvar.')
                    return
                }
                emitToastr('success', 'Alterações salvas.')
                this.initialSnapshot = { image: this.publishMainImage, author: this.publishAuthor }
                this.$emit('submit', resp?.message?.article)
            } catch (err) {
                emitToastr('error', err?.message || 'Erro ao salvar.')
            } finally {
                this.savingMetadata = false
            }
        },
        async cancelSchedule() {
            if (!this.article || this.cancelingSchedule) return
            this.cancelingSchedule = true
            try {
                const resp = await Api.updateArticleMetadataV2({
                    article_id: this.article._id,
                    clear_schedule: true,
                })
                if (resp?.error) {
                    emitToastr('error', resp.message || 'Erro ao cancelar agendamento.')
                    return
                }
                emitToastr('success', 'Agendamento cancelado.')
                this.$emit('submit', resp?.message?.article)
                this.$emit('input', false)
            } catch (err) {
                emitToastr('error', err?.message || 'Erro ao cancelar agendamento.')
            } finally {
                this.cancelingSchedule = false
            }
        },
        hydrateFromArticle(article) {
            if (!article) return
            this.publishMainImage = article.publish_main_image || ''
            this.publishAuthor = article.publish_author || ''
            this.initialSnapshot = { image: this.publishMainImage, author: this.publishAuthor }

            if (article.publish_status === 'scheduled' && article.publish_date) {
                const d = new Date(article.publish_date)
                this.publishMode = 'schedule'
                this.dateValue = format(d, 'yyyy-MM-dd')
                this.timeValue = format(d, 'HH:mm')
            } else {
                this.publishMode = 'now'
                const defaultDate = addHours(new Date(), 1)
                this.dateValue = format(defaultDate, 'yyyy-MM-dd')
                this.timeValue = format(defaultDate, 'HH:mm')
            }
        },
    },
    watch: {
        article: {
            immediate: true,
            handler(a) {
                this.hydrateFromArticle(a)
            },
        },
        value(val) {
            if (val) this.hydrateFromArticle(this.article)
        },
    },
}
</script>

<style scoped>
.publish-card {
    border-radius: var(--radius-lg);
    overflow: hidden;
}

.publish-body {
    padding: 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-height: 70vh;
}

.field-block {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.field-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--color-on-surface-variant);
}

.mode-switch {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.mode-option {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 14px 16px;
    border-radius: var(--radius-sm);
    border: 1.5px solid var(--glass-panel-border);
    background: var(--glass-soft-bg);
    text-align: left;
    cursor: pointer;
    transition: border-color 160ms ease, background 160ms ease, transform 160ms ease;
    font-family: inherit;
}

.mode-option:hover {
    border-color: var(--color-outline-variant);
}

.mode-option--active {
    border-color: var(--color-primary);
    background: rgba(112, 42, 225, 0.08);
}

.mode-option-title {
    font-weight: 700;
    font-size: 14px;
    color: var(--color-on-surface);
}

.mode-option-hint {
    font-size: 12px;
    color: var(--color-on-surface-variant);
}

.shortcut-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.shortcut-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: var(--radius-pill);
    border: 1px solid var(--glass-panel-border);
    background: var(--glass-soft-bg);
    color: var(--color-on-surface-variant);
    font-size: 13px;
    cursor: pointer;
    transition: all 140ms ease;
    font-family: inherit;
}

.shortcut-chip:hover {
    border-color: var(--color-outline-variant);
    color: var(--color-on-surface);
}

.shortcut-chip--active {
    border-color: var(--color-primary);
    background: rgba(112, 42, 225, 0.10);
    color: var(--color-primary);
}

.datetime-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.schedule-warning {
    display: inline-flex;
    align-items: center;
    background: rgba(180, 19, 64, 0.08);
    color: var(--color-error);
    padding: 6px 10px;
    border-radius: var(--radius-xs);
    font-size: 13px;
    margin-top: 4px;
}

.scheduled-info {
    display: flex;
    align-items: center;
    background: rgba(112, 42, 225, 0.08);
    border: 1px solid rgba(112, 42, 225, 0.20);
    padding: 10px 12px;
    border-radius: var(--radius-xs);
    font-size: 13px;
    color: var(--color-on-surface);
}

.published-info {
    display: flex;
    align-items: flex-start;
    background: var(--color-success-soft);
    border: 1px solid rgba(16, 185, 129, 0.2);
    padding: 12px 14px;
    border-radius: var(--radius-xs);
}

.publish-actions {
    padding: 12px 20px;
}

.one-line-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 480px;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: opacity 180ms ease, transform 180ms ease, max-height 260ms ease;
    overflow: hidden;
}

.slide-fade-enter,
.slide-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
    max-height: 0;
}

.slide-fade-enter-to,
.slide-fade-leave {
    max-height: 400px;
}

@media (max-width: 600px) {
    .mode-switch,
    .datetime-row {
        grid-template-columns: 1fr;
    }
}
</style>
